<template>
  <div class="fts-settings-page">
    <header class="fts-settings-head">
      <div>
        <h1>Usage</h1>
        <p>What this workspace holds, what it did, and where the work happened.</p>
      </div>
      <div class="fts-settings-head-actions" v-if="!loading && !unavailable">
        <div class="fts-settings-seg" role="group" aria-label="Period">
          <button
            type="button"
            v-for="option in periods"
            :key="option.days"
            :class="{ 'is-on': option.days === days }"
            :aria-pressed="String(option.days === days)"
            @click="choose(option.days)"
          >
            {{ option.label }}
          </button>
        </div>
        <button
          type="button"
          class="fts-settings-btn"
          :disabled="!hasRollup"
          @click="exportCsv"
        >
          Export CSV
        </button>
      </div>
    </header>

    <div class="fts-settings-loading" v-if="loading">
      <fts-spinner :size="16" />
      <span>Loading&hellip;</span>
    </div>

    <section class="fts-settings-card" v-else-if="unavailable">
      <h2>{{ unavailable.title }}</h2>
      <p class="fts-settings-card-sub">{{ unavailable.body }}</p>
    </section>

    <template v-else>
      <!-- "We have not measured this" and "you did nothing" look identical on a chart, and only
           one of them is true. The rollup runs as a scheduled job; when it has not run there is
           nothing to draw and saying so beats a flat zero line. -->
      <section class="fts-settings-card" v-if="!hasRollup">
        <div>
          <h2>Usage is not being measured yet</h2>
          <p class="fts-settings-card-sub">
            Daily usage is collected by a scheduled job, and it has not produced anything for this
            period. This is not a report of no activity — nothing has been counted.
            <!-- Only when there IS capacity below: with billing switched off there is none. -->
            <template v-if="capacityCells.length">
              Plan capacity below is measured live and is unaffected.
            </template>
          </p>
        </div>
      </section>

      <section class="fts-settings-card" v-if="capacityCells.length">
        <div>
          <h2>Plan capacity</h2>
          <p class="fts-settings-card-sub">
            What your plan includes, and what is in use. Going over never stops anyone working.
          </p>
        </div>

        <div class="fts-settings-capacity">
          <div class="fts-settings-capacity-cell" v-for="cell in capacityCells" :key="cell.key">
            <div class="fts-settings-capacity-label">
              <font-awesome-icon :icon="['fas', cell.icon]" />
              <span>{{ cell.label }}</span>
            </div>
            <div class="fts-settings-capacity-value">
              <span class="fts-settings-capacity-used">{{ cell.used }}</span>
              <span class="fts-settings-capacity-of">{{ cell.of }}</span>
            </div>
            <div class="fts-settings-meter" v-if="cell.pct !== null">
              <div
                class="fts-settings-meter-fill"
                :class="{ 'is-warn': cell.pct >= 80 }"
                :style="{ width: Math.min(100, cell.pct) + '%' }"
              ></div>
            </div>
            <div class="fts-settings-meter-none" v-else></div>
            <div class="fts-settings-capacity-note">{{ cell.note }}</div>
          </div>
        </div>
      </section>

      <template v-if="hasRollup">
        <section class="fts-settings-card">
          <div>
            <h2>Activity</h2>
            <p class="fts-settings-card-sub">{{ activitySummary }}</p>
          </div>

          <div class="fts-settings-bars" role="img" :aria-label="activitySummary">
            <span
              v-for="day in bars"
              :key="day.key"
              class="fts-settings-bar"
              :title="day.title"
            >
              <i :style="{ height: day.height }"></i>
            </span>
          </div>
          <div class="fts-settings-axis" v-if="bars.length">
            <span>{{ bars[0].label }}</span>
            <span>{{ bars[bars.length - 1].label }}</span>
          </div>

          <p class="fts-settings-note">
            One bar per day the job has measured. Who did the work — people, automation or AI —
            is not broken out yet; the rollup does not record it.
          </p>
        </section>

        <section class="fts-settings-card" v-if="recordBars.length > 1">
          <div>
            <h2>Records over time</h2>
            <p class="fts-settings-card-sub">{{ recordsSummary }}</p>
          </div>

          <div class="fts-settings-bars" role="img" :aria-label="recordsSummary">
            <span
              v-for="day in recordBars"
              :key="day.key"
              class="fts-settings-bar is-records"
              :title="day.title"
            >
              <i :style="{ height: day.height }"></i>
            </span>
          </div>
          <div class="fts-settings-axis">
            <span>{{ recordBars[0].label }}</span>
            <span>{{ recordBars[recordBars.length - 1].label }}</span>
          </div>

          <p class="fts-settings-note">{{ recordScaleNote }}</p>
        </section>

        <section class="fts-settings-card" v-if="modules.length">
          <div>
            <h2>By module</h2>
            <p class="fts-settings-card-sub">
              Records as of {{ rolledUpToLabel }}, and activity over the period.
            </p>
          </div>

          <table class="fts-settings-table">
            <thead>
              <tr>
                <th scope="col">Module</th>
                <th scope="col" class="is-amount">Records</th>
                <th scope="col">Share</th>
                <th scope="col" class="is-right">Activity</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="module in modules" :key="module.moduleId">
                <td>{{ module.name }}</td>
                <td class="is-amount">{{ module.recordsLabel }}</td>
                <td>
                  <div class="fts-settings-meter">
                    <div class="fts-settings-meter-fill" :style="{ width: module.share + '%' }"></div>
                  </div>
                </td>
                <td class="is-right">{{ module.activityLabel }}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section class="fts-settings-card" v-if="people.length">
          <div>
            <h2>By person</h2>
            <p class="fts-settings-card-sub">Who did the work over the period.</p>
          </div>

          <table class="fts-settings-table">
            <thead>
              <tr>
                <th scope="col">Person</th>
                <th scope="col" class="is-right">Activity</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="person in people" :key="person.name">
                <td>{{ person.name }}</td>
                <td class="is-right">{{ person.activityLabel }}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <!-- A rebuilt day's entry totals omit rows deleted since it, so the number is
             reconstructed rather than observed. Only shown when a day in range actually is. -->
        <p class="fts-settings-note" v-if="usage.anyBackfilled">
          Some days in this period were rebuilt after the fact. Their record counts leave out
          anything deleted since, so they read a little low.
        </p>
      </template>

      <section class="fts-settings-card" v-if="historyLabel">
        <div>
          <h2>Change history</h2>
          <p class="fts-settings-card-sub">
            Your plan includes {{ historyLabel }} of change history. Older changes move to the
            archive and stay readable.
          </p>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
/**
 * Settings › Usage (proposal §3 D-4).
 *
 * Two sources, deliberately kept apart because they are measured differently:
 *
 *   `api/billing/summary`  — plan capacity, counted LIVE when the page loads.
 *   `api/Workspace/usage`  — the daily rollup, written by a scheduled job (core r33636).
 *
 * The rollup can be absent. It is a cron job, and where it has not run there is nothing to draw:
 * `rolledUpTo` is null and the page says the work has not been MEASURED rather than drawing a
 * flat zero line, which would report "you did nothing". Capacity still shows, because it does not
 * come from the job.
 *
 * Not here, because nothing supports it yet: who did the work (people / automation / AI — the
 * rollup does not record it, §4 #9), credits and storage per module (§4 #3 and a new counter),
 * and usage alerts (§4 #6). Each is a real gap, and inventing any of them from the numbers that
 * do exist would be a guess presented as a measurement.
 */
import { commonStore } from "../../store/commonStore";
import FTSSpinner from "../../elements/FTSSpinner";

const PERIODS = [
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "12 months" },
];

export default {
  name: "SettingsUsage",
  components: { "fts-spinner": FTSSpinner },
  props: {
    workspaceId: [String, Number],
  },
  data() {
    return {
      loading: true,
      unavailable: null,
      summary: null,
      usage: null,
      days: 30,
      periods: PERIODS,
    };
  },
  computed: {
    hasRollup() {
      return !!(this.usage && this.usage.rolledUpTo && (this.usage.daily || []).length);
    },
    rolledUpToLabel() {
      return this.usage && this.usage.rolledUpTo ? this.date(this.usage.rolledUpTo) : "";
    },
    capacity() {
      return (this.summary && this.summary.capacity) || null;
    },
    /**
     * The same four dimensions Overview shows, with what actually happens at the limit. Nothing
     * in this build refuses anything for being over capacity — no upload, no invite, no record —
     * so none of these notes may imply otherwise.
     */
    capacityCells() {
      const c = this.capacity;
      if (!c) return [];

      const cells = [];
      const people = c.people || {};
      cells.push(this.cell("people", "user-friends", "People", people.used, people.included,
        "Adding people is never blocked. Actor seats are counted separately."));

      const records = c.activeRecords || {};
      cells.push(this.cell("records", "table-cells-large", "Active records", records.used, records.included,
        "Never blocks work — records can always be created, updated and closed."));

      const storage = c.storageGb || {};
      cells.push(this.cell("storage", "server", "File storage", storage.used, storage.included,
        "Uploads are not blocked at this figure.", " GB"));

      if (c.historyDays) {
        cells.push({
          key: "history",
          icon: "clock-rotate-left",
          label: "Change history",
          used: this.historyLabel,
          of: "",
          pct: null,
          note: "Older changes move to the archive and stay readable.",
        });
      }
      return cells;
    },
    historyLabel() {
      const days = this.capacity && this.capacity.historyDays;
      if (!days) return "";
      if (days >= 360) {
        const years = Math.round(days / 365);
        return years + (years === 1 ? " year" : " years");
      }
      return days + " days";
    },
    totalActivity() {
      return (this.usage && this.usage.totalActivity) || 0;
    },
    activitySummary() {
      if (!this.hasRollup) return "";
      const measured = (this.usage.daily || []).length;
      return this.number(this.totalActivity) + " things happened over "
        + measured + (measured === 1 ? " measured day" : " measured days");
    },
    bars() {
      return this.barsOf((this.usage && this.usage.daily) || [], "activity", "activity");
    },
    recordBars() {
      const days = (this.usage && this.usage.daily) || [];
      const values = days.map(d => Number(d.records) || 0);
      if (values.length < 2) return [];
      const low = Math.min.apply(null, values);
      const high = Math.max.apply(null, values);
      const span = high - low;
      return days.map((d, i) => {
        const value = Number(d.records) || 0;
        return {
          key: d.date || i,
          // A floor of 12% so the lowest day is still a bar to compare against, not a gap.
          height: (span > 0 ? 12 + ((value - low) / span) * 88 : 50) + "%",
          label: this.shortDate(d.date),
          title: this.shortDate(d.date) + " — " + this.number(value) + " records"
            + (d.backfilled ? " (rebuilt)" : ""),
        };
      });
    },
    /**
     * Two counts of the same thing sit on this page and will not agree: capacity above is
     * counted LIVE when the page loads, this one is the rollup's snapshot as of its last day.
     * Both say when they were taken, so the difference reads as two moments rather than a bug.
     */
    recordsSummary() {
      const days = (this.usage && this.usage.daily) || [];
      if (days.length < 2) return "";
      const first = Number(days[0].records) || 0;
      const last = Number(days[days.length - 1].records) || 0;
      const delta = last - first;
      const asOf = this.rolledUpToLabel ? " as of " + this.rolledUpToLabel : "";
      if (delta === 0) return this.number(last) + " records" + asOf + ", unchanged over the period";
      return this.number(last) + " records" + asOf + ", "
        + (delta > 0 ? "up " : "down ") + this.number(Math.abs(delta)) + " over the period";
    },
    /**
     * A running total that moves by 2% over a month is a flat line on a zero-based axis, which
     * shows nothing at all. This one is scaled between its own lowest and highest day, so the
     * shape is visible — and the note says so, because a zoomed axis can otherwise read as a
     * dramatic climb.
     */
    recordScaleNote() {
      const days = (this.usage && this.usage.daily) || [];
      const values = days.map(d => Number(d.records) || 0);
      if (values.length < 2) return "";
      const low = Math.min.apply(null, values);
      const high = Math.max.apply(null, values);
      if (high === low) return "Unchanged across every measured day.";
      return "Scaled between " + this.number(low) + " and " + this.number(high)
        + " to make the change visible — the bars do not start at zero.";
    },
    modules() {
      const list = (this.usage && this.usage.byModule) || [];
      const biggest = list.reduce((m, x) => Math.max(m, Number(x.records) || 0), 0);
      return list.map(m => ({
        moduleId: m.moduleId,
        name: m.name || ("Module " + m.moduleId),
        recordsLabel: this.number(m.records),
        activityLabel: this.number(m.activity),
        // Share of the biggest module, so the bars compare against each other rather than
        // against a plan limit that may not exist.
        share: biggest > 0 ? Math.round(((Number(m.records) || 0) / biggest) * 100) : 0,
      }));
    },
    people() {
      return ((this.usage && this.usage.byPerson) || []).map(p => ({
        name: p.name,
        activityLabel: this.number(p.activity),
      }));
    },
  },
  methods: {
    api() {
      return commonStore.state.client;
    },
    cell(key, icon, label, used, included, note, unit) {
      const suffix = unit || "";
      const hasLimit = included !== null && included !== undefined && Number(included) > 0;
      return {
        key,
        icon,
        label,
        used: this.number(used) + suffix,
        // No cap is not a cap of zero: an arrangement off the plan ladder must not draw a full
        // bar or raise a warning.
        of: hasLimit ? "of " + this.number(included) + suffix : "no limit",
        pct: hasLimit ? ((Number(used) || 0) / Number(included)) * 100 : null,
        note,
      };
    },
    barsOf(days, field, kind) {
      const max = days.reduce((m, d) => Math.max(m, Number(d[field]) || 0), 0);
      if (!max) return [];
      return days.map((d, i) => {
        const value = Number(d[field]) || 0;
        return {
          key: d.date || i,
          // A measured day with nothing in it still gets a sliver, so it reads as measured
          // rather than as a gap in the chart.
          height: Math.max(2, (value / max) * 100) + "%",
          label: this.shortDate(d.date),
          title: this.shortDate(d.date) + " — " + this.number(value) + " " + kind
            + (d.backfilled ? " (rebuilt)" : ""),
        };
      });
    },
    number(value) {
      const n = Number(value || 0);
      return (Math.round(n * 10) / 10).toLocaleString("en-US");
    },
    date(value) {
      const d = new Date(value);
      return isNaN(d.getTime())
        ? ""
        : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    },
    shortDate(value) {
      const d = new Date(value);
      return isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
    },
    choose(days) {
      if (days === this.days) return;
      this.days = days;
      this.loadUsage();
    },
    /**
     * The numbers on the page, as a file. Built from what is already loaded rather than from a
     * second endpoint — there is nothing here the page has not already been given.
     */
    exportCsv() {
      if (!this.hasRollup) return;
      const rows = [["Date", "Activity", "Records", "Rebuilt"]];
      (this.usage.daily || []).forEach(d => {
        rows.push([this.isoDay(d.date), d.activity, d.records, d.backfilled ? "yes" : ""]);
      });
      rows.push([]);
      rows.push(["Module", "Records", "Activity"]);
      (this.usage.byModule || []).forEach(m => {
        rows.push([m.name || ("Module " + m.moduleId), m.records, m.activity]);
      });
      rows.push([]);
      rows.push(["Person", "Activity"]);
      (this.usage.byPerson || []).forEach(p => rows.push([p.name, p.activity]));

      const csv = rows.map(r => r.map(this.csvCell).join(",")).join("\r\n");
      const name = "usage-" + this.workspaceId + "-" + this.days + "d.csv";
      this.download(csv, name);
    },
    csvCell(value) {
      const text = value === null || value === undefined ? "" : String(value);
      return /[",\r\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
    },
    isoDay(value) {
      const d = new Date(value);
      return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
    },
    download(text, filename) {
      // A BOM so Excel opens it as UTF-8 rather than mangling any non-ASCII module name.
      const blob = new Blob(["﻿" + text], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    async load() {
      this.loading = true;
      this.unavailable = null;
      try {
        const res = await this.api().get("/api/billing/summary", {
          params: { workspaceId: this.workspaceId },
        });
        this.summary = res.data;
      } catch (err) {
        const status = err && err.response && err.response.status;
        // 404 is billing switched off, which says nothing about usage — the page carries on and
        // simply has no capacity to show.
        if (status === 403) {
          this.unavailable = {
            title: "Only a workspace admin can see usage",
            body: "Ask a workspace admin if you need these figures.",
          };
          this.loading = false;
          return;
        }
        this.summary = null;
      }

      await this.loadUsage();
      this.loading = false;
    },
    async loadUsage() {
      try {
        const res = await this.api().get("/api/Workspace/usage", {
          params: { workspaceId: this.workspaceId, days: this.days },
        });
        this.usage = res.data;
      } catch (err) {
        const status = err && err.response && err.response.status;
        if (status === 403) {
          this.unavailable = {
            title: "Only a workspace admin can see usage",
            body: "Ask a workspace admin if you need these figures.",
          };
        }
        // Anything else — including a server that predates this endpoint — leaves the rollup
        // absent, which the page already knows how to say.
        this.usage = null;
      }
    },
  },
  watch: {
    workspaceId() {
      this.usage = null;
      this.summary = null;
      this.load();
    },
  },
  mounted() {
    this.load();
  },
};
</script>
